import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FiArrowRight, FiX, FiTarget } from 'react-icons/fi';
import {
    overlayVariants,
    modalVariants,
    getPhaseConfig,
    SurefireEagle,
    AnimatedProgressTrack,
    RepairAnimation,
    SurefireTitle,
    SurefirePhase1Card,
} from './surefireFeedback';

const SurefireInSessionPopup = ({
    isOpen, message, progress, atomName, onContinue, onGoToPanel,
    unit = 'surefire',
    panelLabel = 'Przejdź do panelu pewniaków →',
    learningTarget = 3, consolidationTarget = 2,
}) => {
    const reduced = useReducedMotion();
    if (!isOpen) return null;

    const config = getPhaseConfig(progress, message, unit, learningTarget);
    const isPhase1 = config.type === 'phase1';

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9990] flex items-center justify-center px-4">
                    <motion.div
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="absolute inset-0 backdrop-blur-sm bg-black/70"
                        onClick={onContinue}
                    />

                    <motion.div
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="relative z-[9991] bg-white w-full max-w-sm rounded-2xl p-6 shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] border border-gray-100 text-center overflow-hidden"
                    >
                        <button
                            onClick={onContinue}
                            className="absolute top-3 right-3 p-2 text-gray-300 hover:text-gray-500 transition-colors rounded-full hover:bg-gray-50 z-10"
                        >
                            <FiX size={20} />
                        </button>

                        <SurefireEagle type={config.type} reduced={reduced} />

                        {atomName && (
                            <motion.div
                                initial={reduced ? false : { opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="flex items-center justify-center gap-1.5 mb-3"
                            >
                                <FiTarget size={12} className="text-gray-400" />
                                <span className="text-[10px] font-bold font-apotek tracking-[0.15em] uppercase text-gray-400">
                                    {atomName}
                                </span>
                            </motion.div>
                        )}

                        {config.type === 'repetition' && <RepairAnimation reduced={reduced} />}

                        {config.type !== 'repetition' && (
                            <AnimatedProgressTrack
                                progress={progress || learningTarget}
                                phaseType={config.type}
                                reduced={reduced}
                                learningTarget={learningTarget}
                                consolidationTarget={consolidationTarget}
                            />
                        )}

                        <SurefireTitle config={config} reduced={reduced} />

                        {isPhase1 && <SurefirePhase1Card reduced={reduced} />}

                        <motion.button
                            initial={reduced ? false : { opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 }}
                            onClick={onContinue}
                            className="w-full group flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold font-apotek tracking-wider uppercase transition-all active:scale-[0.98] shadow-lg relative z-10 bg-[#233855] text-white hover:bg-[#1a2b42] shadow-[#233855]/20"
                        >
                            <span>Super, robię kolejne zadania</span>
                            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>

                        <motion.button
                            initial={reduced ? false : { opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.9 }}
                            onClick={onGoToPanel}
                            className="mt-3 py-2 text-xs text-gray-400 hover:text-[#233855] transition-colors font-medium tracking-wide"
                        >
                            {panelLabel}
                        </motion.button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default SurefireInSessionPopup;
